import type { RunDTO, RunTrigger } from "../../shared/types";
import { get } from "../lib/api";
import { scopePath, shortId } from "../lib/scopes";
import { navigate } from "../lib/router";
import { MoreOnScroll, usePaged } from "./MoreOnScroll";
import { Table } from "./Table";
import { TextLink } from "./TextLink";
import { RunCost } from "./CostBreakdown";
import { Badge, ErrorText, StatusBadge, timeAgo } from "./ui";

/** How a run was started, in the words a person uses. */
const STARTED_BY: Record<RunTrigger, string> = {
  unknown: "—",
  chat: "Chat",
  slack: "Slack",
  email: "Email",
  meeting: "Meeting",
  task: "Task",
  workflow: "Workflow",
  schedule: "Schedule",
  delegation: "Delegation",
  api_client: "API client",
  wake: "Wake",
  trigger: "Trigger",
  system: "System",
};

/** The first line that says something, cut to what one row holds. */
const oneLine = (text: string | null | undefined) => (text ?? "").split("\n").map((l) => l.trim()).find(Boolean)?.slice(0, 240) ?? "";

/** What the row says it did: the run's answer, else why it failed, else the task it was given. */
function answerLine(run: RunDTO) {
  const answer = oneLine(run.result);
  if (answer) return <span>{answer}</span>;
  const failure = run.error && typeof run.error === "object" ? oneLine(String((run.error as { message?: unknown }).message ?? "")) : oneLine(typeof run.error === "string" ? run.error : "");
  if (failure) return <span className="text-danger">{failure}</span>;
  return <span className="text-muted">{oneLine(run.task) || "No answer yet."}</span>;
}

/**
 * The runs of one scope (every agent under it) or of one agent (every scope it works under), newest first, 50 at a
 * time as the end scrolls into view. Each row opens the run's own page, with every step it took. A scope's page shows
 * which agent ran it; an agent's page shows the scope.
 */
export function RunsList({ filter, show, hint }: { filter: { agentId: string } | { subjectId: string }; show: "agent" | "scope"; hint: string }) {
  const query = Object.entries(filter).map(([k, v]) => `${k}=${v}`).join("&");
  const { rows, done, loading, error, more } = usePaged<RunDTO>((before) => get(`/api/runs?${query}&limit=50${before ? `&before=${before}` : ""}`), [query]);
  return (
    <div className="space-y-2">
      <Table
        title="Runs"
        hint={hint}
        rows={rows ?? []}
        rowKey={(r) => r.id}
        onRowClick={(r) => navigate(`/admin/runs/${r.id}`)}
        empty={rows ? "No runs yet." : "Loading…"}
        columns={[
          { header: "Answer", primary: true, cell: (r) => <div className="line-clamp-2 whitespace-normal">{answerLine(r)}</div> },
          { header: "Started", className: "text-xs whitespace-nowrap text-muted", cell: (r) => <span title={new Date(r.createdAt).toLocaleString()}>{timeAgo(r.createdAt)}</span> },
          {
            header: "How",
            className: "text-xs whitespace-nowrap text-muted",
            cell: (r) => (r.parentRunId ? `${STARTED_BY[r.trigger]} · delegated` : STARTED_BY[r.trigger]),
          },
          show === "agent"
            ? { header: "Agent", className: "text-xs whitespace-nowrap", cell: (r) => <TextLink to={`/admin/agents/${r.agentId}`} onClick={(e) => e.stopPropagation()}>{r.agentName}</TextLink> }
            : {
                header: "Scope",
                className: "text-xs whitespace-nowrap",
                cell: (r) =>
                  r.subject ? (
                    <TextLink to={scopePath(r.subject)} onClick={(e) => e.stopPropagation()}>
                      {r.subject.name || shortId(r.subject.slug)}
                    </TextLink>
                  ) : (
                    <Badge>default</Badge>
                  ),
              },
          { header: "Status", cell: (r) => <StatusBadge status={r.status} /> },
          { header: "Cost", align: "right", className: "text-xs tabular-nums", cell: (r) => <RunCost usd={r.costUsd} paidBy={r.paidBy} /> },
        ]}
      />
      <ErrorText error={error} />
      <MoreOnScroll onMore={more} done={done} loading={loading} />
    </div>
  );
}
